import React from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { SectionHeading } from '../components/ui/SectionHeading';
import { motion } from 'framer-motion';
import { Heart, Droplet, Utensils, Moon } from 'lucide-react';
const recoveryTips = [{
  icon: Droplet,
  title: 'Rehydrate Properly',
  description: 'Drink plenty of water and electrolyte solutions. Your body is dehydrated from high altitude and exertion.',
  timeline: 'Days 1-3'
}, {
  icon: Utensils,
  title: 'Nourish Your Body',
  description: 'Eat nutrient-rich foods, especially proteins and complex carbs. Replenish vitamins and minerals lost during the trek.',
  timeline: 'Days 1-7'
}, {
  icon: Moon,
  title: 'Rest & Sleep',
  description: 'Allow your body adequate rest. Sleep 8-10 hours per night for the first week. Avoid strenuous activity.',
  timeline: 'Days 1-7'
}, {
  icon: Heart,
  title: 'Gradual Activity',
  description: 'Resume normal exercise gradually. Start with light walking, then slowly increase intensity over 2-3 weeks.',
  timeline: 'Weeks 2-4'
}];
export function PostTrekRecoveryPage() {
  return <div className="min-h-screen bg-white font-sans" data-id="element-1911">
      <Navbar data-id="element-1912" />

      {/* Hero Section */}
      <div className="relative h-[60vh] min-h-[500px] flex items-center justify-center" data-id="element-1913">
        <div className="absolute inset-0 bg-cover bg-center z-0" style={{
        backgroundImage: 'url("https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?q=80&w=2020&auto=format&fit=crop")'
      }} data-id="element-1914">
          <div className="absolute inset-0 bg-gradient-to-b from-teal-900/70 via-teal-900/50 to-teal-900/80" data-id="element-1915" />
        </div>
        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto" data-id="element-1916">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} className="flex justify-center mb-6" data-id="element-1917">
            <div className="w-16 h-16 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center" data-id="element-1918">
              <Heart className="text-white" size={32} data-id="element-1919" />
            </div>
          </motion.div>
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="text-5xl md:text-6xl font-bold mb-6 tracking-tight" data-id="element-1920">
            Post-Trek Recovery & Care
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.4
        }} className="text-xl md:text-2xl text-teal-100 font-light max-w-2xl mx-auto" data-id="element-1921">
            Take care of your body after the adventure of a lifetime
          </motion.p>
        </div>
      </div>

      {/* Overview */}
      <section className="py-20 px-4 max-w-4xl mx-auto" data-id="element-1922">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1923">
          <SectionHeading title="The Importance of Recovery" subtitle="Overview" data-id="element-1924" />
          <div className="prose prose-lg max-w-none text-stone-700 leading-relaxed space-y-4" data-id="element-1925">
            <p data-id="element-1926">
              Completing the Everest Base Camp trek is an incredible
              achievement, but your journey isn't quite over. Your body has been
              through significant physical stress—high altitude, long days of
              hiking, temperature extremes, and reduced oxygen levels all take
              their toll.
            </p>
            <p data-id="element-1927">
              Proper post-trek recovery is essential for your health and helps
              prevent long-term issues. Most trekkers feel exhausted for several
              days after returning to lower altitudes. This is completely
              normal. Your body needs time to readjust to normal oxygen levels
              and repair itself.
            </p>
            <p data-id="element-1928">
              This guide will help you recover effectively, address common
              post-trek issues, and return to your normal routine safely and
              comfortably.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Recovery Timeline */}
      <section className="bg-teal-50 py-20 px-4" data-id="element-1929">
        <div className="max-w-7xl mx-auto" data-id="element-1930">
          <SectionHeading title="Recovery Timeline" subtitle="What to expect" data-id="element-1931" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1932">
            {recoveryTips.map((tip, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="bg-white rounded-2xl p-8 shadow-md" data-id="element-1933">
                <div className="flex items-start gap-4 mb-4" data-id="element-1934">
                  <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl flex items-center justify-center shrink-0" data-id="element-1935">
                    <tip.icon size={28} className="text-white" data-id="element-1936" />
                  </div>
                  <div data-id="element-1937">
                    <h3 className="text-xl font-bold text-stone-900 mb-1" data-id="element-1938">
                      {tip.title}
                    </h3>
                    <span className="text-sm text-teal-600 font-semibold" data-id="element-1939">
                      {tip.timeline}
                    </span>
                  </div>
                </div>
                <p className="text-stone-700 leading-relaxed" data-id="element-1940">
                  {tip.description}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Common Issues */}
      <section className="py-20 px-4 max-w-4xl mx-auto" data-id="element-1941">
        <SectionHeading title="Common Post-Trek Issues" subtitle="And how to address them" data-id="element-1942" />
        <div className="space-y-6" data-id="element-1943">
          <div className="bg-blue-50 rounded-xl p-6 border-l-4 border-blue-600" data-id="element-1944">
            <h3 className="text-lg font-bold text-blue-900 mb-3" data-id="element-1945">
              Fatigue & Exhaustion
            </h3>
            <p className="text-blue-800 leading-relaxed mb-3" data-id="element-1946">
              Feeling extremely tired for 3-7 days is normal. Your body worked
              hard at altitude with less oxygen.
            </p>
            <p className="text-blue-800 leading-relaxed font-semibold" data-id="element-1947">
              Solution: Rest, sleep 8-10 hours nightly, avoid caffeine, and
              don't rush back to normal activities.
            </p>
          </div>

          <div className="bg-green-50 rounded-xl p-6 border-l-4 border-green-600" data-id="element-1948">
            <h3 className="text-lg font-bold text-green-900 mb-3" data-id="element-1949">
              Muscle Soreness & Joint Pain
            </h3>
            <p className="text-green-800 leading-relaxed mb-3" data-id="element-1950">
              Aching muscles and joints, especially knees and ankles, are common
              from the descent.
            </p>
            <p className="text-green-800 leading-relaxed font-semibold" data-id="element-1951">
              Solution: Gentle stretching, warm baths, anti-inflammatory
              medication if needed, massage therapy.
            </p>
          </div>

          <div className="bg-amber-50 rounded-xl p-6 border-l-4 border-amber-600" data-id="element-1952">
            <h3 className="text-lg font-bold text-amber-900 mb-3" data-id="element-1953">
              Digestive Issues
            </h3>
            <p className="text-amber-800 leading-relaxed mb-3" data-id="element-1954">
              Stomach upset, changes in appetite, or digestive discomfort can
              persist for a few days.
            </p>
            <p className="text-amber-800 leading-relaxed font-semibold" data-id="element-1955">
              Solution: Eat bland, easily digestible foods. Probiotics can help.
              Stay hydrated. See a doctor if severe.
            </p>
          </div>

          <div className="bg-purple-50 rounded-xl p-6 border-l-4 border-purple-600" data-id="element-1956">
            <h3 className="text-lg font-bold text-purple-900 mb-3" data-id="element-1957">
              Sleep Disruption
            </h3>
            <p className="text-purple-800 leading-relaxed mb-3" data-id="element-1958">
              Difficulty sleeping or unusual sleep patterns as your body
              readjusts to normal altitude.
            </p>
            <p className="text-purple-800 leading-relaxed font-semibold" data-id="element-1959">
              Solution: Maintain regular sleep schedule, avoid screens before
              bed, create relaxing bedtime routine.
            </p>
          </div>
        </div>
      </section>

      {/* Nutrition */}
      <section className="bg-stone-50 py-20 px-4" data-id="element-1960">
        <div className="max-w-4xl mx-auto" data-id="element-1961">
          <SectionHeading title="Post-Trek Nutrition" subtitle="Foods that help recovery" data-id="element-1962" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1963">
            <div className="bg-white rounded-xl p-6" data-id="element-1964">
              <h3 className="font-bold text-stone-900 mb-3" data-id="element-1965">
                Protein-Rich Foods
              </h3>
              <p className="text-stone-600 mb-3" data-id="element-1966">
                Help repair muscle tissue damaged during the trek.
              </p>
              <ul className="text-stone-700 space-y-1" data-id="element-1967">
                <li data-id="element-1968">• Lean meats, fish, eggs</li>
                <li data-id="element-1969">• Beans, lentils, tofu</li>
                <li data-id="element-1970">• Greek yogurt, cottage cheese</li>
              </ul>
            </div>
            <div className="bg-white rounded-xl p-6" data-id="element-1971">
              <h3 className="font-bold text-stone-900 mb-3" data-id="element-1972">
                Complex Carbohydrates
              </h3>
              <p className="text-stone-600 mb-3" data-id="element-1973">
                Replenish glycogen stores and provide sustained energy.
              </p>
              <ul className="text-stone-700 space-y-1" data-id="element-1974">
                <li data-id="element-1975">• Whole grains, oats, quinoa</li>
                <li data-id="element-1976">• Sweet potatoes, brown rice</li>
                <li data-id="element-1977">• Whole wheat bread, pasta</li>
              </ul>
            </div>
            <div className="bg-white rounded-xl p-6" data-id="element-1978">
              <h3 className="font-bold text-stone-900 mb-3" data-id="element-1979">
                Fruits & Vegetables
              </h3>
              <p className="text-stone-600 mb-3" data-id="element-1980">
                Provide vitamins, minerals, and antioxidants for healing.
              </p>
              <ul className="text-stone-700 space-y-1" data-id="element-1981">
                <li data-id="element-1982">• Berries, citrus fruits</li>
                <li data-id="element-1983">• Leafy greens, broccoli</li>
                <li data-id="element-1984">• Bananas (potassium)</li>
              </ul>
            </div>
            <div className="bg-white rounded-xl p-6" data-id="element-1985">
              <h3 className="font-bold text-stone-900 mb-3" data-id="element-1986">Healthy Fats</h3>
              <p className="text-stone-600 mb-3" data-id="element-1987">
                Support inflammation reduction and overall recovery.
              </p>
              <ul className="text-stone-700 space-y-1" data-id="element-1988">
                <li data-id="element-1989">• Avocados, nuts, seeds</li>
                <li data-id="element-1990">• Olive oil, fatty fish</li>
                <li data-id="element-1991">• Nut butters</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* When to See a Doctor */}
      <section className="py-20 px-4 max-w-4xl mx-auto" data-id="element-1992">
        <SectionHeading title="When to Seek Medical Attention" subtitle="Warning signs" data-id="element-1993" />
        <div className="bg-red-50 rounded-2xl p-8 border-l-4 border-red-600" data-id="element-1994">
          <h3 className="text-xl font-bold text-red-900 mb-4" data-id="element-1995">
            Contact a doctor if you experience:
          </h3>
          <ul className="space-y-3" data-id="element-1996">
            {['Persistent cough with colored phlegm or blood', "Severe headaches that don't improve", 'Chest pain or difficulty breathing', 'High fever or signs of infection', 'Severe or worsening digestive issues', "Swelling in legs or feet that doesn't reduce", 'Any symptoms that concern you or worsen over time'].map((symptom, index) => <li key={index} className="flex items-start gap-3" data-id="element-1997">
                <span className="text-red-600 font-bold text-xl" data-id="element-1998">!</span>
                <span className="text-red-800" data-id="element-1999">{symptom}</span>
              </li>)}
          </ul>
        </div>
      </section>

      {/* Mental Recovery */}
      <section className="bg-teal-50 py-20 px-4" data-id="element-2000">
        <div className="max-w-4xl mx-auto" data-id="element-2001">
          <SectionHeading title="Mental & Emotional Recovery" subtitle="The psychological aspect" data-id="element-2002" />
          <div className="bg-white rounded-2xl p-8" data-id="element-2003">
            <p className="text-stone-700 leading-relaxed mb-4" data-id="element-2004">
              It's common to experience a range of emotions after completing a
              major trek. You might feel a sense of accomplishment mixed with
              sadness that the adventure is over. Some trekkers experience
              "post-trek blues" as they return to normal life.
            </p>
            <p className="text-stone-700 leading-relaxed mb-4" data-id="element-2005">
              Give yourself time to process the experience. Look through photos,
              share stories with friends, and journal about your journey. Stay
              connected with trek mates if possible. Consider planning your next
              adventure—having something to look forward to helps with the
              transition.
            </p>
            <p className="text-stone-700 leading-relaxed font-semibold" data-id="element-2006">
              Remember: What you accomplished was extraordinary. Be proud, be
              patient with yourself, and allow your body and mind the time they
              need to fully recover.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 bg-gradient-to-r from-teal-600 to-cyan-600 text-white" data-id="element-2007">
        <div className="max-w-4xl mx-auto text-center" data-id="element-2008">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-2009">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-2010">
              Ready for Your Next Adventure?
            </h2>
            <p className="text-xl text-teal-100 mb-8" data-id="element-2011">
              Once you've recovered, start planning your next Himalayan journey
            </p>
            <a href="#plan-trip" data-id="element-2012">
              <button className="bg-white text-teal-600 px-8 py-4 rounded-full font-semibold hover:bg-teal-50 transition-colors" data-id="element-2013">
                Explore More Treks
              </button>
            </a>
          </motion.div>
        </div>
      </section>

      <Footer data-id="element-2014" />
    </div>;
}